import React, { useEffect, useMemo, useState } from 'react'

const ALL_CATEGORY = '전체'
const FALLBACK_CATEGORY = '기타'
const FAVORITE_TEMPLATE_KEY = 'sgez:customer-memo-template-favorites'

function templateKey(template) {
  return String(template?.id || [template?.category, template?.title].filter(Boolean).join(':'))
}

function templateCategory(template) {
  return String(template?.category || FALLBACK_CATEGORY).trim() || FALLBACK_CATEGORY
}

function templateTags(template) {
  return Array.isArray(template?.tags) ? template.tags.filter(Boolean).slice(0, 3) : []
}

function loadFavoriteTemplateKeys() {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage?.getItem(FAVORITE_TEMPLATE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return []
  }
}

export default function CustomerMemoTemplatePicker({ templates = [], disabled = false, onApply }) {
  const [favoriteKeys, setFavoriteKeys] = useState(loadFavoriteTemplateKeys)
  const rows = useMemo(() => (Array.isArray(templates) ? templates.filter((item) => item && item.title) : []), [templates])
  const categories = useMemo(() => {
    const values = new Set()
    rows.forEach((item) => values.add(templateCategory(item)))
    return [ALL_CATEGORY, ...Array.from(values)]
  }, [rows])
  const [category, setCategory] = useState(ALL_CATEGORY)

  useEffect(() => {
    try { window.localStorage?.setItem(FAVORITE_TEMPLATE_KEY, JSON.stringify(favoriteKeys)) } catch { }
  }, [favoriteKeys])

  const favoriteKeySet = useMemo(() => new Set(favoriteKeys), [favoriteKeys])
  const sortedRows = useMemo(() => rows.slice().sort((a, b) => Number(b.usageCount || 0) - Number(a.usageCount || 0) || Number(a.sortOrder || 0) - Number(b.sortOrder || 0) || String(a.title || '').localeCompare(String(b.title || ''))), [rows])
  const visible = useMemo(() => {
    const filtered = category === ALL_CATEGORY ? sortedRows : sortedRows.filter((item) => templateCategory(item) === category)
    return filtered.slice().sort((a, b) => Number(favoriteKeySet.has(templateKey(b))) - Number(favoriteKeySet.has(templateKey(a))) || sortedRows.indexOf(a) - sortedRows.indexOf(b))
  }, [category, favoriteKeySet, sortedRows])
  const recommended = useMemo(() => {
    const favorites = sortedRows.filter((item) => favoriteKeySet.has(templateKey(item)))
    const merged = [...favorites, ...sortedRows.filter((item) => !favoriteKeySet.has(templateKey(item)))]
    return merged.slice(0, 4)
  }, [favoriteKeySet, sortedRows])

  const toggleFavorite = (template) => {
    const key = templateKey(template)
    setFavoriteKeys((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [key, ...prev].slice(0, 20)))
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
        사용 가능한 템플릿이 없습니다.
      </div>
    )
  }

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap gap-1">
          {categories.map((item) => (
            <button
              key={item}
              type="button"
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${category === item ? 'bg-brand-navy text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'}`}
              disabled={disabled}
              onClick={() => setCategory(item)}
            >
              {item}
            </button>
          ))}
        </div>
        <select
          className="min-w-[10rem] rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-medium disabled:opacity-50"
          value=""
          disabled={disabled}
          onChange={(e) => {
            const selected = rows.find((item) => templateKey(item) === e.target.value)
            if (selected) onApply?.(selected)
          }}
        >
          <option value="">템플릿 선택</option>
          {categories.filter((item) => item !== ALL_CATEGORY).map((group) => (
            <optgroup key={group} label={group}>
              {rows.filter((item) => templateCategory(item) === group).map((template) => (
                <option key={templateKey(template)} value={templateKey(template)}>{template.title}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      {recommended.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {recommended.map((template) => {
            const favorite = favoriteKeySet.has(templateKey(template))
            return (
              <button
                key={`recommended:${templateKey(template)}`}
                type="button"
                className="max-w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-left text-xs hover:border-brand-sky hover:bg-white disabled:opacity-50"
                disabled={disabled}
                onClick={() => onApply?.(template)}
                title={template.summary || template.title}
              >
                <span className="font-semibold text-slate-900">{favorite ? '★ ' : ''}{template.title}</span>
                <span className="ml-1 text-[11px] text-slate-500">{templateCategory(template)}</span>
              </button>
            )
          })}
        </div>
      ) : null}
      {visible.length > 0 ? (
        <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {visible.slice(0, 6).map((template) => {
            const key = templateKey(template)
            const favorite = favoriteKeySet.has(key)
            return (
              <div key={`visible:${key}`} className="min-w-0 rounded-md bg-white px-2.5 py-2 text-xs ring-1 ring-slate-200 hover:ring-brand-sky">
                <div className="flex min-w-0 items-start gap-2">
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left disabled:opacity-50"
                    disabled={disabled}
                    onClick={() => onApply?.(template)}
                  >
                    <span className="block truncate font-semibold text-slate-900">{template.title}</span>
                    {template.summary ? <span className="mt-0.5 block truncate text-[11px] text-slate-500">{template.summary}</span> : null}
                  </button>
                  <button
                    type="button"
                    className={`shrink-0 rounded px-1 text-sm ${favorite ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`}
                    disabled={disabled}
                    onClick={() => toggleFavorite(template)}
                    aria-label={favorite ? '즐겨찾기 해제' : '즐겨찾기'}
                    title={favorite ? '즐겨찾기 해제' : '즐겨찾기'}
                  >
                    {favorite ? '★' : '☆'}
                  </button>
                </div>
                {templateTags(template).length > 0 ? (
                  <span className="mt-1 flex flex-wrap gap-1 text-[10px] text-slate-400">
                    {templateTags(template).map((tag) => <span key={`${key}:${tag}`}>#{tag}</span>)}
                  </span>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}